import { Injectable, Inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Config } from '../config/env.config';
import { BaseHttpService } from '../base/_base-http.service';
import * as _ from 'lodash';

import { contentHeaders } from 'common/headers';
import { FitmentsSearchModel } from './fitments-search.model';
import { getFiltersQueryString, getVcdbFiltersQueryString, getSearchParamsFromQuery } from '../search/search-helpers';
import { VEHICLE_FILTER_TYPE, VCDB_FILTER_TYPE } from '../search/active-filters/active-filter-types';
import { YEAR_START_TYPE, YEAR_END_TYPE, MAKE_TYPE } from '../search/advanced-search/filter-types';
import { SearchModel } from '../search/search.model';
import { ActiveFilterModel } from '../search/active-filters/active-filter.model';
import {catchError, delay, map} from 'rxjs/operators';
import {forkJoin} from 'rxjs/internal/observable/forkJoin';
import {of} from 'rxjs/internal/observable/of';


@Injectable()
export class FitmentsService extends BaseHttpService {

  fetch(brandId: string, itemId: number, searchModel: FitmentsSearchModel): Observable<any> {
    return this.http.get(
      `${Config.API}/items/${brandId}/${itemId.toString()}/apps?${this.getQueryString(searchModel)}`
    )
    .pipe(catchError(this.handleError.bind(this)));
  }

  getQueryString(searchModel: FitmentsSearchModel): string {
    const vehicleFilters = _.filter(searchModel.filters, ['type', VEHICLE_FILTER_TYPE]);
    const vcdbFilters = _.filter(searchModel.filters, ['type', VCDB_FILTER_TYPE]);
    return 'page=' + searchModel.currentPage +
      '&itemsPerPage=' + searchModel.itemsPerPage +
      '&search=' + searchModel.search +
      '&sortCol=' + searchModel.sortCol +
      '&sortDir=' + searchModel.sortDir +
      getFiltersQueryString(vehicleFilters) +
      getVcdbFiltersQueryString(vcdbFilters);
  }

  fetchYmmPredictiveSearch(search: string, page: number = 1, perPage: number = 50): Observable<any> {
    return this.http.get(`${Config.API}/fitments/ymm-predictive-search-pagination` +
      `?search=${search}&perPage=${perPage}&page=${page}`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  fetchYmmFilters(attributeName: string, fieldNames?: any): Observable<any> {
    return this.http.get(
      `${Config.API}/fitments/advance-filter/${attributeName}?${this.getFieldNamesQuery(fieldNames)}`
      )
        .pipe(catchError(this.handleError.bind(this)));
  }

  fetchProductYmmFilters(brandId: any, productId: any, attributeName: string, fieldNames?: any): Observable<any> {
    return this.http.get(
      `${Config.API}/fitments/${brandId}/item-detail/${productId}/${attributeName}?${this.getFieldNamesQuery(fieldNames)}`
      )
        .pipe(catchError(this.handleError.bind(this)));
  }

  fetchFiltersByStatus(brandId: any, status: string, attributeName: string, fieldNames: any): Observable<any> {
    return this.http.get(
      `${Config.API}/fitments/${brandId}/${status}/${attributeName}?${this.getFieldNamesQuery(fieldNames)}`
      )
        .pipe(catchError(this.handleError.bind(this)));
  }

  fetchFieldsList(fieldNames?: any): Observable<any> {
    return this.http.get(`${Config.API}/fitments/fields-list?${this.getFieldNamesQuery(fieldNames)}`)
        .pipe(catchError(this.handleError.bind(this)));
  }

  fetchOptionsFieldsList(fieldNames?: any): Observable<any> {
    return this.fetchFieldsList(fieldNames)
      .pipe(map((res: any) => {
        return _.map(_.values(res), (item: any) => {
          item.option = item.attribute_name;
          return item;
        });
      }));
  }

  fetchProductFieldsList(fieldNames: any, brandId: any, productId: any, itemName: string): Observable<any> {
    return this.http.get(
        `${Config.API}/fitments/fields-list?${this.getFieldNamesQuery(fieldNames)}` +
        `&brandRelationshipId=${brandId}` +
        `&item=${itemName}` +
        `&id=${productId}`
      )
        .pipe(catchError(this.handleError.bind(this)));
  }

  fetchProductOptionsFieldsList(fieldNames: any, brandId: any, productId: any, itemName: string): Observable<any> {
      return this.fetchProductFieldsList(fieldNames, brandId, productId, itemName)
        .pipe(map((res: any) => {
          return _.map(_.values(res), (item: any) => {
            item.option = item.attribute_name;
            return item;
          });
        }));
    }

  getFieldNamesQuery(fieldNames?: any): string {
    if (!fieldNames) { return ''; }
    return _.reduce(fieldNames, (result: string, value: any, key: string) => {
      if (_.isArray(value)) {
        if (key === MAKE_TYPE) {
          return _.reduce(value, (memo: string, val: any) => {
            return `${memo}${!memo ? '' : '&'}${key}[]=${val}`;
          }, result);
        } else {
          value = (_.size(value) > 0) ? encodeURI(JSON.stringify(value)) : '';
        }
      }
      return `${result}${!result ? '' : '&'}${key}=${value || ''}`;
    }, '');
  }

  assignAllAppsInSearchToProduct(brandId: any, itemId: any, searchQuery: string): Observable<any> {
    const data = this.getSearchParamsFromQuery(searchQuery);
    data.insert_query_results = true;
    if (!_.isArray(itemId)) {
      itemId = [itemId];
    }
    const body = JSON.stringify({ data, itemIds: itemId });
    return this.http.post(
      `${Config.API}/items/add-fitment-app/${brandId}`,
      body,
      {headers: contentHeaders}
    )
        .pipe(catchError(this.handleError.bind(this)));
  }

  assignAllAppsInSearchToProducts(brandId: any, itemIds: Array<any>, searchQuery: string): Observable<any> {
    const requests = _.map(itemIds, (itemId: any) => {
      return this.assignAllAppsInSearchToProduct(brandId, itemId, searchQuery);
    });
    return forkJoin(requests)
        .pipe(catchError(this.handleError.bind(this)));
  }

  assignToProduct(brandId: any, itemId: any, assignmentValues: Array<any>, columns: Array<string>): Observable<any> {
    const data = _.map(assignmentValues, (item: any) => {
      item.columns = columns;
      return item;
    });
    const body = JSON.stringify({ data });
    return this.http.post(
      `${Config.API}/items/add-fitment-app/${brandId}/${itemId}`,
      body,
      {headers: contentHeaders}
    )
        .pipe(catchError(this.handleError.bind(this)));
  }

  assignToProducts(brandId: any, itemIds: Array<any>, data: Array<any>, columns: Array<string>): Observable<any> {
    const requests = _.map(itemIds, (itemId: any) => {
      return this.assignToProduct(brandId, itemId, data, columns);
    });
    return forkJoin(requests)
        .pipe(catchError(this.handleError.bind(this)));
  }

  assignToProductList(brandId: any, listId: any, assignmentValues: Array<any>, columns: Array<string>): Observable<any> {
    const data = _.each(assignmentValues, (item: any) => {
      item.columns = columns;
    });
    const body = JSON.stringify({ data });
    return this.http.post(
      `${Config.API}/data-lists/add-fitment-app/${brandId}/${listId}`,
      body,
      {headers: contentHeaders}
    )
        .pipe(catchError(this.handleError.bind(this)));
  }

  assignToProductLists(brandId: any, listIds: Array<any>, data: Array<any>, columns: Array<string>): Observable<any> {
    const requests = _.map(listIds, (listId: any) => {
      return this.assignToProductList(brandId, listId, data, columns);
    });
    return forkJoin(requests)
        .pipe(catchError(this.handleError.bind(this)));
  }


  assignAllAppsInSearchToProductLists(brandId: any, listIds: Array<string>, searchQuery: string): Observable<any> {
    const requests = _.map(listIds, (listId: any) => {
      return this.assignAllAppsInSearchToProductList(brandId, listId, searchQuery);
    });
    return forkJoin(requests)
        .pipe(catchError(this.handleError.bind(this)));
  }

  assignAllAppsInSearchToProductList(brandId: any, dataListIds: any, searchQuery: string, fitmentListId: any= ''): Observable<any> {

    const data = this.getSearchParamsFromQuery(searchQuery);
    data.insert_query_results = true;
    if (fitmentListId) {
      data.is_fitment_list = true;
      data.item_id = fitmentListId;
      data.fitment_list_id = fitmentListId;
    }

    const body = JSON.stringify({ data, dataListIds: dataListIds });
    return this.http.post(
      `${Config.API}/data-lists/add-fitment-app/${brandId}`,
      body,
      {headers: contentHeaders}
    )
        .pipe(catchError(this.handleError.bind(this)));
  }

  assignListToProduct(brandId: any, itemId: any, listId: any, searchQuery: string= ''): Observable<any> {
    const data = this.getSearchParamsFromQuery(searchQuery);
    const body = JSON.stringify({ data });
    return this.http.post(
      `${Config.API}/fitment-list/assign-to-item/${brandId}/${listId}/${itemId}`,
      body,
      {headers: contentHeaders}
    )
        .pipe(catchError(this.handleError.bind(this)));
  }

  assignListToProducts(brandId: any, itemIds: Array<any>, listId: any, searchQuery: string= ''): Observable<any> {
    const data = this.getSearchParamsFromQuery(searchQuery);
    const body = JSON.stringify({ itemIds, data });
    return this.http.post(
      `${Config.API}/fitment-list/assign-to-item/${brandId}/${listId}`,
      body,
      {headers: contentHeaders}
    )
      .pipe(catchError(this.handleError.bind(this)));
  }

  removeItemDetailApps(brandId: any, itemId: any, data: Array<any>): Observable<any> {
    const body = JSON.stringify({ data });
    return this.http.post(
      `${Config.API}/items/remove-apps/${brandId}/${itemId}`,
      body,
      {headers: contentHeaders}
    )
        .pipe(catchError(this.handleError.bind(this)));
  }

  removeAllAppsInSearch(brandId: any, itemId: any, searchQuery: string): Observable<any> {
    const data = this.getSearchParamsFromQuery(searchQuery);
    data.remove_query_results = true;
    delete data.columns;
    const body = JSON.stringify({ data });
    return this.http.post(
      `${Config.API}/items/remove-apps/${brandId}/${itemId}`,
      body,
      {headers: contentHeaders}
    )
        .pipe(catchError(this.handleError.bind(this)));
  }

  updateApps(brandId: any, itemId: any, data: any): Observable<any> {
    const body = JSON.stringify({ data });
    return this.http.post(
      `${Config.API}/items/update-fitment-app/${brandId}/${itemId}`,
      body,
      {headers: contentHeaders}
    )

        .pipe(catchError(this.handleError.bind(this)));
  }

  getPositions(brandId: any, itemId: any): Observable<any> {
    return this.http.get(`${Config.API}/items/positions/${brandId}/${itemId}`)
        .pipe(catchError(this.handleError.bind(this)));
  }

  getPositionsByFitmentId(fitmentId: string): Observable<any> {
    return this.http.get(`${Config.API}/fitments/fitment-positions/${fitmentId}`)
        .pipe(catchError(this.handleError.bind(this)));
  }

  fetchProductFitments(brandId: any, productId: any, searchModel: FitmentsSearchModel): Observable<any> {
    return this.http.get(`${Config.API}/fitments/${brandId}/item-detail/${productId}?${this.getQueryString(searchModel)}`)
        .pipe(catchError(this.handleError.bind(this)));
  }

  replaceApp(brandId: any, itemId: any, data: any): Observable<any> {
    return this.http.post(
      `${Config.API}/items/replace-app/${brandId}/${itemId}`,
      JSON.stringify(data),
      {headers: contentHeaders}
    )
        .pipe(catchError(this.handleError.bind(this)));
  }

  replaceAppMultipleColumns(brandId: any, itemId: any, data: any): Observable<any> {
    return this.http.post(
      `${Config.API}/items/replace-app-multiple-columns/${brandId}/${itemId}`,
      JSON.stringify(data),
      {headers: contentHeaders}
    )
        .pipe(catchError(this.handleError.bind(this)));
  }

  removeColumnsFromApp(brandId: any, itemId: any, columns: Array<string>): Observable<any> {
    return this.http.post(
      `${Config.API}/items/remove-columns-from-apps/${brandId}/${itemId}`,
      JSON.stringify({'remove_columns': columns}),
      {headers: contentHeaders}
    )
        .pipe(catchError(this.handleError.bind(this)));
  }

  getFitmentStatuses(brandId: any, status: string): Observable<any> {
    return of([
      {
        title: 'Approved (coming soon)',
        option: 1
      },
      {
        title: 'Pending (coming soon)',
        option: 2
      },
    ]).pipe(delay(1000));
    // return this.http.get(`${Config.API}/fitments/${brandId}/statuses/${status}`)
    //   .catch(this.handleError.bind(this));
  }

  private getSearchParamsFromQuery(searchQuery: string): any {
    return _.omit(
      getSearchParamsFromQuery(searchQuery),
      ['page', 'itemsPerPage', 'sortCol', 'sortDir']
    );
  }

}
